import '../work-order.scss';
import Icon from '../../../components/icons/icon';
import { useEffect, useState } from 'react';
import {
  ScheduledService,
  ScheduledServiceSearchRequest,
} from '../../../clients/quipli-service/service-and-repair/service-and-repair';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { PaginationData } from '../../../clients/quipli-service/pagination/pagination';
import { DataTable, DataTableRow, DataTableColumn } from '../../../components/table/data-table';
import { Typography } from '../../../components/typography/typography';
import { SearchBar } from '../../../components/search-bar/search-bar';
import { Button } from '../../../components/form/button';
import { Dropdown } from '../../../components/dropdown/dropdown';
import { NavLink, useSearchParams } from 'react-router-dom';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { displayHumanDuration } from '../../../util/util';
import { QuipliLink } from '../../../components/link/quipli-link';

export const ScheduledServiceList = () => {
  const { getConfiguration, searchScheduledServices } = useAPI();

  // States
  const [isListLoading, setListLoading] = useState<boolean>(true);
  const [data, setData] = useState<DataTableRow[]>([]);
  const [pagination, setPagination] = useState<PaginationData>();
  const [unitTrackingField, setUnitTrackingField] = useState<'unitNumber' | 'serialNumber'>();
  const [tableColumns, setTableColumns] = useState<DataTableColumn[]>([]);

  // List request hooks
  const [params] = useSearchParams();
  const { request, setRequest } = useRequestQueryParams<ScheduledServiceSearchRequest>(
    {
      keyword: params.has('keyword') ? params.get('keyword')! : undefined,
      pageSize: params.has('pageSize') ? parseInt(params.get('pageSize')!) : 10,
      pageNumber: params.has('pageNumber') ? parseInt(params.get('pageNumber')!) : 1,
    },
    async (_request) => {
      setListLoading(true);

      const { pagination, items } = await searchScheduledServices(_request as any);
      setData(items.map(buildScheduledServiceDataRow));
      setPagination(pagination);
      setTableColumns(scheduledServiceColumns);

      setListLoading(false);
    },
    [unitTrackingField],
  );

  const scheduledServiceColumns: DataTableColumn[] = [
    { key: 'name', label: 'Name' },
    { key: 'type', label: 'Work Type' },
    { key: 'unitStatus', label: 'Unit Status' },
    { key: 'occurrence', label: 'Occur Every' },
    { key: 'units', label: 'Product Units' },
  ];

  const buildScheduledServiceDataRow = (ssr: ScheduledService): DataTableRow => {
    return {
      mobileHeader: (
        <>
          <div className="product-container">
            <NavLink
              to={`/manager/repair/scheduled-services/${ssr.id}`}
              className="underline underline-offset-1 text-[var(--quipli-black-800)]"
            >
              {ssr.serviceName}
            </NavLink>
          </div>
        </>
      ),
      fields: {
        name: (
          <div className="product-container">
            <Typography type="text-sm" className="text-quipli-black-800">
              {ssr.serviceName}
            </Typography>
          </div>
        ),
        type: ssr.workOrderType.label,
        unitStatus: ssr.unitStatus,
        occurrence:
          ssr.frequencyInterval && ssr.machineHoursInterval
            ? `Every ${displayHumanDuration(ssr.frequencyInterval)} or ${ssr.machineHoursInterval} hours`
            : ssr.frequencyInterval
            ? `Every ${displayHumanDuration(ssr.frequencyInterval)}`
            : `Every ${ssr.machineHoursInterval} hours`,
        units: (
          <>
            {Object.keys(ssr.productUnits).map((productId) => {
              const productDetails = ssr.productUnits[parseInt(productId)];
              return (
                <div key={productId}>
                  <Typography type="text-sm" className="text-quipli-black-800">
                    {
                      productDetails.name //product name
                    }
                  </Typography>
                  <div className="grid grid-flow-col">
                    {productDetails.units.map((unit) => (
                      <div key={unit.productUnit.id}>
                        <Typography type="text-xs" className="text-grey-500">
                          {unit.productUnit[unitTrackingField!]}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        ),
        id: ssr.id,
      },
      className: 'cursor-pointer',
      redirectTo: `repair/scheduled-services/${ssr.id}`,
    };
  };

  // Effects
  useEffect(() => {
    getConfiguration('CONF_UNIT_TRACKING_VIA').then((config) => {
      setUnitTrackingField(config?.value === '1' ? 'serialNumber' : 'unitNumber');
    });
  }, []);

  return (
    <div className="work-order">
      <div className="container-v2">
        <div className="flex justify-between items-center">
          <Typography type="display-sm" weight="medium">
            Scheduled Services
          </Typography>

          <div className="block md:hidden">
            <Dropdown
              trigger={() => (
                <Button className="button-tertiary">
                  <Icon icon="DotsVertical" />
                </Button>
              )}
            >
              <QuipliLink className="block md:hidden" to="/repair/scheduled-services/create">
                Add Scheduled Service Repair
              </QuipliLink>
            </Dropdown>
          </div>
        </div>

        <div className="pt-5 flex items-center justify-between">
          <SearchBar
            placeholder={'Search Scheduled Services'}
            defaultValue={request.keyword}
            setRequest={setRequest}
            className="mb-6"
          />

          <div className="hidden md:block">
            <Dropdown
              trigger={() => (
                <Button className="button-tertiary">
                  <Icon icon="DotsVertical" />
                </Button>
              )}
            >
              <QuipliLink to="repair/scheduled-services/create">Add Scheduled Service Repair</QuipliLink>
            </Dropdown>
          </div>
        </div>

        <DataTable
          isLoading={isListLoading}
          columnNames={tableColumns}
          data={data}
          pagination={pagination}
          request={request}
          setRequest={setRequest}
        />
      </div>
    </div>
  );
};
